import { getChatCompletion } from './aiIntegrations/chatCompletion';

/**
 * Clean markdown code blocks from AI response
 * Handles cases where AI wraps JSON in ```json ... ```
 */
function cleanJsonResponse(content) {
  if (!content || content === 'undefined') {
    return null;
  }
  
  // Remove markdown code blocks if present
  let cleaned = content?.trim();
  
  // Remove ```json or ``` at start
  if (cleaned?.startsWith('```json')) {
    cleaned = cleaned?.slice(7);
  } else if (cleaned?.startsWith('```')) {
    cleaned = cleaned?.slice(3);
  }
  
  // Remove ``` at end
  if (cleaned?.endsWith('```')) {
    cleaned = cleaned?.slice(0, -3);
  }
  
  return cleaned?.trim();
}

const RETAIL_SYSTEM_PROMPT = `You are a personal finance advisor AI. Analyze the user's financial data and provide:
1. Personalized Financial Nudges: 2-3 actionable insights based on spending patterns
2. Savings Optimization: 2-3 specific suggestions to increase savings
3. Behavioral Insights: 2-3 observations about financial habits and risks

Format your response as JSON with this structure:
{
  "nudges": [{"title": "string", "description": "string", "impact": "string", "priority": "high|medium|low"}],
  "savings": [{"title": "string", "description": "string", "potential": "string", "timeframe": "string"}],
  "insights": [{"title": "string", "description": "string", "risk": "high|medium|low", "category": "string"}]
}`;

const ENTERPRISE_SYSTEM_PROMPT = `You are an enterprise financial wellness advisor AI. Analyze workforce financial data and provide:
1. Employee Wellness Insights: 2-3 observations about employee financial health trends
2. Risk Mitigation Strategies: 2-3 recommendations to reduce financial stress
3. Engagement Opportunities: 2-3 suggestions to improve program participation

Format your response as JSON with this structure:
{
  "wellness": [{"title": "string", "description": "string", "impact": "string", "departments": ["string"]}],
  "risks": [{"title": "string", "description": "string", "severity": "high|medium|low", "affected": "string"}],
  "engagement": [{"title": "string", "description": "string", "expectedOutcome": "string", "effort": "high|medium|low"}]
}`;

const PARTNER_SYSTEM_PROMPT = `You are a retail analytics AI advisor. Analyze consumer behavior and campaign data to provide:
1. Consumer Behavior Analytics: 2-3 insights about customer spending patterns
2. Campaign Optimization: 2-3 recommendations to improve conversion rates
3. Revenue Growth Opportunities: 2-3 strategies to increase partner revenue

Format your response as JSON with this structure:
{
  "behavior": [{"title": "string", "description": "string", "segment": "string", "trend": "string"}],
  "campaigns": [{"title": "string", "description": "string", "expectedLift": "string", "priority": "high|medium|low"}],
  "growth": [{"title": "string", "description": "string", "potential": "string", "timeline": "string"}]
}`;

export async function generateRetailRecommendations(financialData) {
  try {
    const userPrompt = `Analyze this financial data and provide personalized recommendations:

Financial Health Score: ${financialData?.healthScore}/100
Savings Ratio: ${financialData?.savingsRatio}
Debt-to-Income: ${financialData?.debtToIncome}
Emergency Fund: ${financialData?.emergencyFund}
Monthly Income: $${financialData?.monthlyIncome}
Monthly Expenses: $${financialData?.monthlyExpenses}

Top Spending Categories:
${financialData?.topCategories?.map(cat => `- ${cat?.name}: $${cat?.spent}/$${cat?.budget}`)?.join('\n')}`;

    const response = await getChatCompletion(
      'GEMINI',
      'gemini/gemini-2.5-flash',
      [
        { role: 'system', content: RETAIL_SYSTEM_PROMPT },
        { role: 'user', content: userPrompt }
      ],
      {
        max_tokens: 1500,
        temperature: 1,
        response_format: {
          type: 'json_schema',
          json_schema: {
            name: 'RetailRecommendations',
            schema: {
              type: 'object',
              properties: {
                nudges: {
                  type: 'array',
                  items: {
                    type: 'object',
                    properties: {
                      title: { type: 'string' },
                      description: { type: 'string' },
                      impact: { type: 'string' },
                      priority: { type: 'string', enum: ['high', 'medium', 'low'] }
                    },
                    required: ['title', 'description', 'impact', 'priority'],
                    additionalProperties: false
                  }
                },
                savings: {
                  type: 'array',
                  items: {
                    type: 'object',
                    properties: {
                      title: { type: 'string' },
                      description: { type: 'string' },
                      potential: { type: 'string' },
                      timeframe: { type: 'string' }
                    },
                    required: ['title', 'description', 'potential', 'timeframe'],
                    additionalProperties: false
                  }
                },
                insights: {
                  type: 'array',
                  items: {
                    type: 'object',
                    properties: {
                      title: { type: 'string' },
                      description: { type: 'string' },
                      risk: { type: 'string', enum: ['high', 'medium', 'low'] },
                      category: { type: 'string' }
                    },
                    required: ['title', 'description', 'risk', 'category'],
                    additionalProperties: false
                  }
                }
              },
              required: ['nudges', 'savings', 'insights'],
              additionalProperties: false
            },
            strict: true
          }
        }
      }
    );

    const content = response?.choices?.[0]?.message?.content;
    
    // Validate content exists before parsing
    if (!content || content === 'undefined') {
      console.error('No valid content in AI response:', response);
      return {
        nudges: [
          {
            title: 'AI Service Unavailable',
            description: 'Unable to generate recommendations at this time. Please try again later.',
            action: 'Retry later',
            impact: 'Service temporarily unavailable'
          }
        ],
        savings: [],
        insights: []
      };
    }
    
    return JSON.parse(cleanJsonResponse(content));
  } catch (error) {
    console.error('Error generating retail recommendations:', error);
    throw error;
  }
}

export async function generateEnterpriseRecommendations(workforceData) {
  try {
    const userPrompt = `Analyze this workforce financial wellness data and provide recommendations:

Total Employees: ${workforceData?.totalEmployees}
Average Wellness Score: ${workforceData?.avgWellnessScore}/100
High Risk Employees: ${workforceData?.highRiskCount} (${workforceData?.highRiskPercentage}%)
Program Engagement Rate: ${workforceData?.engagementRate}%
Improvement Rate: ${workforceData?.improvementRate}%

Department Risk Levels:
${workforceData?.departments?.map(dept => `- ${dept?.name}: ${dept?.employees} employees, Risk Level ${dept?.riskLevel}/10, Avg Score: ${dept?.avgScore}`)?.join('\n')}`;

    const response = await getChatCompletion(
      'GEMINI',
      'gemini/gemini-2.5-flash',
      [
        { role: 'system', content: ENTERPRISE_SYSTEM_PROMPT },
        { role: 'user', content: userPrompt }
      ],
      {
        max_tokens: 1500,
        temperature: 1,
        response_format: {
          type: 'json_schema',
          json_schema: {
            name: 'EnterpriseRecommendations',
            schema: {
              type: 'object',
              properties: {
                wellness: {
                  type: 'array',
                  items: {
                    type: 'object',
                    properties: {
                      title: { type: 'string' },
                      description: { type: 'string' },
                      impact: { type: 'string' },
                      departments: { type: 'array', items: { type: 'string' } }
                    },
                    required: ['title', 'description', 'impact', 'departments'],
                    additionalProperties: false
                  }
                },
                risks: {
                  type: 'array',
                  items: {
                    type: 'object',
                    properties: {
                      title: { type: 'string' },
                      description: { type: 'string' },
                      severity: { type: 'string', enum: ['high', 'medium', 'low'] },
                      affected: { type: 'string' }
                    },
                    required: ['title', 'description', 'severity', 'affected'],
                    additionalProperties: false
                  }
                },
                engagement: {
                  type: 'array',
                  items: {
                    type: 'object',
                    properties: {
                      title: { type: 'string' },
                      description: { type: 'string' },
                      expectedOutcome: { type: 'string' },
                      effort: { type: 'string', enum: ['high', 'medium', 'low'] }
                    },
                    required: ['title', 'description', 'expectedOutcome', 'effort'],
                    additionalProperties: false
                  }
                }
              },
              required: ['wellness', 'risks', 'engagement'],
              additionalProperties: false
            },
            strict: true
          }
        }
      }
    );

    const content = response?.choices?.[0]?.message?.content;
    
    // Validate content exists before parsing
    if (!content || content === 'undefined') {
      console.error('No valid content in AI response:', response);
      return {
        wellness: [
          {
            title: 'AI Service Unavailable',
            description: 'Unable to generate enterprise recommendations at this time. Please try again later.',
            impact: 'Service temporarily unavailable',
            departments: []
          }
        ],
        risks: [],
        engagement: []
      };
    }
    
    return JSON.parse(cleanJsonResponse(content));
  } catch (error) {
    console.error('Error generating enterprise recommendations:', error);
    throw error;
  }
}

export async function generatePartnerRecommendations(analyticsData) {
  try {
    const userPrompt = `Analyze this retail partner analytics data and provide recommendations:

Total Revenue: $${analyticsData?.totalRevenue}
Total Conversions: ${analyticsData?.totalConversions}
Conversion Rate: ${analyticsData?.conversionRate}%
Average Order Value: $${analyticsData?.avgOrderValue}
Active Customers: ${analyticsData?.activeCustomers}
Campaign ROI: ${analyticsData?.campaignROI}%

Top Campaigns:
${analyticsData?.topCampaigns?.map(camp => `- ${camp?.name}: ${camp?.conversions} conversions, $${camp?.revenue} revenue, ${camp?.roi} ROI`)?.join('\n')}

Customer Segments:
${analyticsData?.segments?.map(seg => `- ${seg?.name}: ${seg?.value} customers (${seg?.percentage}%)`)?.join('\n')}`;

    const response = await getChatCompletion(
      'GEMINI',
      'gemini/gemini-2.5-flash',
      [
        { role: 'system', content: PARTNER_SYSTEM_PROMPT },
        { role: 'user', content: userPrompt }
      ],
      {
        max_tokens: 1500,
        temperature: 1,
        response_format: {
          type: 'json_schema',
          json_schema: {
            name: 'PartnerRecommendations',
            schema: {
              type: 'object',
              properties: {
                behavior: {
                  type: 'array',
                  items: {
                    type: 'object',
                    properties: {
                      title: { type: 'string' },
                      description: { type: 'string' },
                      segment: { type: 'string' },
                      trend: { type: 'string' }
                    },
                    required: ['title', 'description', 'segment', 'trend'],
                    additionalProperties: false
                  }
                },
                campaigns: {
                  type: 'array',
                  items: {
                    type: 'object',
                    properties: {
                      title: { type: 'string' },
                      description: { type: 'string' },
                      expectedLift: { type: 'string' },
                      priority: { type: 'string', enum: ['high', 'medium', 'low'] }
                    },
                    required: ['title', 'description', 'expectedLift', 'priority'],
                    additionalProperties: false
                  }
                },
                growth: {
                  type: 'array',
                  items: {
                    type: 'object',
                    properties: {
                      title: { type: 'string' },
                      description: { type: 'string' },
                      potential: { type: 'string' },
                      timeline: { type: 'string' }
                    },
                    required: ['title', 'description', 'potential', 'timeline'],
                    additionalProperties: false
                  }
                }
              },
              required: ['behavior', 'campaigns', 'growth'],
              additionalProperties: false
            },
            strict: true
          }
        }
      }
    );

    const content = response?.choices?.[0]?.message?.content;
    
    // Validate content exists before parsing
    if (!content || content === 'undefined') {
      console.error('No valid content in AI response:', response);
      return {
        behavior: [
          {
            title: 'AI Service Unavailable',
            description: 'Unable to generate partner recommendations at this time. Please try again later.',
            segment: 'All',
            trend: 'N/A'
          }
        ],
        campaigns: [],
        growth: []
      };
    }
    
    return JSON.parse(cleanJsonResponse(content));
  } catch (error) {
    console.error('Error generating partner recommendations:', error);
    throw error;
  }
}
